
import { defineComponent, onBeforeMount, reactive } from 'vue'

// copmponents
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

// module components
import DirectionsActions from '@/modules/DirectionsModule/components/DirectionsActions/index.vue'

// realisations
import { directions } from '@/core/realisations/directions.realisation'
import { cities } from '@/core/realisations/cities.realisation'

// types
import { DirectionsCreatePayloadT, DirectionT } from '@/core/types/directions.types'
import { CityT } from '@/core/types/cities.types'

// classes
import { DataState } from '@/core/classes/DataState.class'

// constants
import { DirectionsColumns } from '@/core/constants/columns/Directions.columns'

// composable
import useActions from '@/common/composable/useActions'
import useNotifications from '@/common/composable/useNotifications'
import useAccount from '@/common/composable/useAccount'

export default defineComponent({
    components: {
        PageTitle,
        TheButton,
        FailedFetch,
        DirectionsActions,
        TheSelect,
        Cheap,
        TableScrollWrapper,
        TheTable,
    },
    setup() {
        const notifications = useNotifications()
        const { havePermissions } = useAccount()

        const state = reactive(new DataState<DirectionT>({}))

        const filters_state = reactive({
            limit: 50,
            //
            from_city_id: null as number | null,
            where_city_id: null as number | null,
        })
        
        const data_state = reactive({
            from_cities: new DataState<CityT>({}),
            where_cities: new DataState<CityT>({}),
        })

        const loadDirections = async (page: number) => {
            state.pagination.current_page = page

            try {
                state.is_loading = true

                const params = {
                    page,
                    fromCityId: filters_state.from_city_id || undefined,
                    whereCityId: filters_state.where_city_id || undefined,
                    limit: filters_state.limit,
                }

                const response = await directions.getList(params)

                state.list = response.data
                state.pagination.total_pages = response.meta.last_page
                state.pagination.total_items = response.meta.total

                state.is_failed = false
            } catch (error) {
                state.failure.catchError(error)

                if (!state.failure.error_state.is_cancel) {
                    state.is_failed = true
                }
            } finally {
                if (!state.failure.error_state.is_cancel) {
                    state.is_loading = false
                }
            }
        }

        const deleteDirection = async (id: number) => {
            const answer = confirm('Вы уверены, что хотите удалить направление?')
            if (!answer) return

            try {
                state.is_loading = true

                await directions.deleteDirection(id)
                notifications.notify({ title: 'Направление успешно удалено!', type: 'success' })

                loadDirections(1)
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: `Ошибка удаления направления`,
                })
            } finally {
                state.is_loading = false
            }
        }

        const loadCities = async () => {
            try {
                data_state.from_cities.is_loading = true
                data_state.where_cities.is_loading = true

                const data = await cities.getAllList(1)

                data_state.from_cities.list = data.data
                data_state.where_cities.list = data.data

                data_state.from_cities.pagination.total_pages = data.meta.last_page
                data_state.where_cities.pagination.total_pages = data.meta.last_page

                data_state.from_cities.is_failed = false
                data_state.where_cities.is_failed = false

                data_state.from_cities.failure.error_state.is_cancel = false
                data_state.where_cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.from_cities.failure.catchError(error)
                data_state.where_cities.failure.catchError(error)

                if (
                    !data_state.from_cities.failure.error_state.is_cancel &&
                    !data_state.where_cities.failure.error_state.is_cancel
                ) {
                    data_state.from_cities.is_failed = true
                    data_state.where_cities.is_failed = true
                }
            } finally {
                if (
                    !data_state.from_cities.failure.error_state.is_cancel &&
                    !data_state.where_cities.failure.error_state.is_cancel
                ) {
                    data_state.from_cities.is_loading = false
                    data_state.where_cities.is_loading = false
                }
            }
        }

        const onLimitChange = (limit: number) => {
            filters_state.limit = limit
            loadDirections(state.pagination.current_page)
        }

        onBeforeMount(() => {
            loadDirections(1)
            loadCities()
        })

        const { actions_state, startCreate, startUpdate } = useActions<DirectionT>()

        const createDirection = async (payload: DirectionsCreatePayloadT) => {
            try {
                actions_state.is_actions_loading = true

                await directions.createDirection(payload)
                await loadDirections(state.pagination.current_page)

                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const updateDirection = async ({
            id,
            zoneId,
            isNearbyCities,
        }: {
            id: number
            zoneId: number
            isNearbyCities: number
        }) => {
            try {
                actions_state.is_actions_loading = true

                await directions.updateDirection(id, zoneId, isNearbyCities)

                await loadDirections(state.pagination.current_page)

                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const resetFilters = async () => {
            filters_state.from_city_id = null
            filters_state.where_city_id = null

            await loadDirections(1)
        }

        const loadFromCities = async (page: number, search?: string) => {
            data_state.from_cities.pagination.current_page = page

            try {
                data_state.from_cities.is_loading = true

                const data = await cities.getAllList(page, search)
                data_state.from_cities.list = data.data

                data_state.from_cities.pagination.total_pages = data.meta.last_page

                data_state.from_cities.is_failed = false
                data_state.from_cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.from_cities.failure.catchError(error)

                if (!data_state.from_cities.failure.error_state.is_cancel) {
                    data_state.from_cities.is_failed = true
                }
            } finally {
                if (!data_state.from_cities.failure.error_state.is_cancel) {
                    data_state.from_cities.is_loading = false
                }
            }
        }

        const loadMoreFromCities = async (page: number) => {
            if (page > data_state.from_cities.pagination.total_pages) return
            data_state.from_cities.pagination.current_page = page

            try {
                data_state.from_cities.is_loading = true

                const response = await cities.getAllList(page)
                data_state.from_cities.list = data_state.from_cities.list.concat(response.data)

                data_state.from_cities.pagination.total_pages = response.meta.last_page

                data_state.from_cities.is_failed = false
                data_state.from_cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.from_cities.failure.catchError(error)

                if (!data_state.from_cities.failure.error_state.is_cancel) {
                    data_state.from_cities.is_failed = true
                }
            } finally {
                if (!data_state.from_cities.failure.error_state.is_cancel) {
                    data_state.from_cities.is_loading = false
                }
            }
        }

        const searchFromCity = (search_query: string) => {
            // data_state.from_cities.pagination.current_page
            loadFromCities(1, search_query)
        }

        const unshiftFindedFromCity = async (city: CityT) => {
            await loadFromCities(data_state.from_cities.pagination.current_page)
            data_state.from_cities.list.unshift(city)
        }

        const loadWhereCities = async (page: number, search?: string) => {
            data_state.where_cities.pagination.current_page = page

            try {
                data_state.where_cities.is_loading = true

                const data = await cities.getAllList(page, search)
                data_state.where_cities.list = data.data

                data_state.where_cities.pagination.total_pages = data.meta.last_page

                data_state.where_cities.is_failed = false
                data_state.where_cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.where_cities.failure.catchError(error)

                if (!data_state.where_cities.failure.error_state.is_cancel) {
                    data_state.where_cities.is_failed = true
                }
            } finally {
                if (!data_state.where_cities.failure.error_state.is_cancel) {
                    data_state.where_cities.is_loading = false
                }
            }
        }

        const loadMoreWhereCities = async (page: number) => {
            if (page > data_state.where_cities.pagination.total_pages) return
            data_state.where_cities.pagination.current_page = page

            try {
                data_state.where_cities.is_loading = true

                const response = await cities.getAllList(page)
                data_state.where_cities.list = data_state.where_cities.list.concat(response.data)

                data_state.where_cities.pagination.total_pages = response.meta.last_page

                data_state.where_cities.is_failed = false
                data_state.where_cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.where_cities.failure.catchError(error)

                if (!data_state.where_cities.failure.error_state.is_cancel) {
                    data_state.where_cities.is_failed = true
                }
            } finally {
                if (!data_state.where_cities.failure.error_state.is_cancel) {
                    data_state.where_cities.is_loading = false
                }
            }
        }

        const searchWhereCity = (search_query: string) => {
            // data_state.where_cities.pagination.current_page
            loadWhereCities(1, search_query)
        }

        const unshiftFindedWhereCity = async (city: CityT) => {
            await loadWhereCities(data_state.where_cities.pagination.current_page)
            data_state.where_cities.list.unshift(city)
        }

        const searchDirection = async () => {
            await loadDirections(1)
        }

        const getCheckOrTimesIcon = (is_nearby_cities: number) => {
            return `fas ${
                is_nearby_cities ? 'fa-check text-green-500 mr-4' : 'fa-times text-red-500 mr-4'
            }`
        }

        return {
            state,
            data_state,
            filters_state,
            actions_state,
            loadDirections,
            startCreate,
            startUpdate,
            createDirection,
            updateDirection,
            resetFilters,
            onLimitChange,
            deleteDirection,
            havePermissions,
            //
            loadFromCities,
            loadMoreFromCities,
            searchFromCity,
            unshiftFindedFromCity,
            //
            loadWhereCities,
            loadMoreWhereCities,
            searchWhereCity,
            unshiftFindedWhereCity,
            //
            searchDirection,
            getCheckOrTimesIcon,
            //
            DirectionsColumns,
        }
    },
})
